import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import CodeTiptap from "@tiptap/extension-code";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";
import {
  Bold,
  Code2,
  Heading1,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";

export const getNotesEditorMarkdown = (editor) => {
  if (!editor) return "";
  return editor.storage.markdown?.getMarkdown() || "";
};

const NOTES_EDITOR_STYLES = `
  .notes-editor .ProseMirror {
    min-height: 100%;
    caret-color: rgb(var(--c-mainText));
  }
  .notes-editor .ProseMirror:focus {
    outline: none;
  }
  .notes-editor .ProseMirror p.is-editor-empty:first-child::before {
    color: rgb(var(--c-mutedText) / 0.5);
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
  .notes-editor .ProseMirror p {
    margin: 0 0 0.85rem;
  }
  .notes-editor .ProseMirror h1 {
    font-size: 1.65rem;
    line-height: 2rem;
    margin: 1.35rem 0 0.75rem;
    font-weight: 600;
  }
  .notes-editor .ProseMirror h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    margin: 1.1rem 0 0.65rem;
    font-weight: 600;
  }
  .notes-editor .ProseMirror ul,
  .notes-editor .ProseMirror ol {
    padding-left: 1.4rem;
    margin: 0.75rem 0;
  }
  .notes-editor .ProseMirror ul {
    list-style: disc;
  }
  .notes-editor .ProseMirror ol {
    list-style: decimal;
  }
  .notes-editor .ProseMirror blockquote {
    border-left: 2px solid rgb(var(--c-velBlue) / 0.55);
    color: rgb(var(--c-mutedText));
    margin: 1rem 0;
    padding-left: 1rem;
  }
  .notes-editor .ProseMirror pre {
    background: rgb(var(--c-mainBg) / 0.75);
    border: 1px solid rgb(var(--c-surfaceBorder));
    border-radius: 0.65rem;
    overflow-x: auto;
    padding: 0.85rem;
    margin: 1rem 0;
  }
  .notes-editor .ProseMirror code {
    background: rgb(var(--c-mainButtonBg) / 0.85);
    border-radius: 0.35rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9em;
    padding: 0.12rem 0.3rem;
  }
  .notes-editor .ProseMirror pre code {
    background: transparent;
    border-radius: 0;
    padding: 0;
  }
  .notes-editor .ProseMirror a {
    color: rgb(var(--c-velBlue));
    text-decoration: underline;
  }
`;

const toolbarButtons = [
  {
    label: "Bold",
    icon: Bold,
    isActive: (editor) => editor.isActive("bold"),
    run: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    label: "Italic",
    icon: Italic,
    isActive: (editor) => editor.isActive("italic"),
    run: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    label: "Code",
    icon: Code2,
    isActive: (editor) => editor.isActive("code"),
    run: (editor) => editor.chain().focus().toggleCode().run(),
  },
  {
    label: "Heading 1",
    icon: Heading1,
    isActive: (editor) => editor.isActive("heading", { level: 1 }),
    run: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    label: "Heading 2",
    icon: Heading2,
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
    run: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: "Bulleted list",
    icon: List,
    isActive: (editor) => editor.isActive("bulletList"),
    run: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    label: "Numbered list",
    icon: ListOrdered,
    isActive: (editor) => editor.isActive("orderedList"),
    run: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    label: "Quote",
    icon: Quote,
    isActive: (editor) => editor.isActive("blockquote"),
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
];

const NotesEditor = ({ value = "", onChange, placeholder, disabled = false }) => {
  const applyingExternalValueRef = useRef(false);
  const lastValueRef = useRef(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false,
        heading: { levels: [1, 2, 3] },
      }),
      CodeTiptap,
      TiptapLink.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: "text-velBlue underline cursor-pointer",
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Start writing markdown…",
      }),
      TiptapMarkdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        class: [
          "min-h-full px-5 py-5 text-sm leading-7 text-mainText",
          "prose prose-sm dark:prose-invert max-w-none",
          "focus:outline-none",
        ].join(" "),
      },
    },
    onUpdate: ({ editor: activeEditor }) => {
      if (applyingExternalValueRef.current) return;

      const markdown = getNotesEditorMarkdown(activeEditor);
      lastValueRef.current = markdown;
      onChange?.(markdown);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    const nextValue = value || "";
    if (nextValue === lastValueRef.current) return;

    const currentMarkdown = getNotesEditorMarkdown(editor);
    if (nextValue === currentMarkdown) {
      lastValueRef.current = nextValue;
      return;
    }

    applyingExternalValueRef.current = true;
    editor.commands.setContent(nextValue, false);
    lastValueRef.current = nextValue;
    window.requestAnimationFrame(() => {
      applyingExternalValueRef.current = false;
    });
  }, [editor, value]);

  const setLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Paste a URL", previousUrl);
    if (url === null) return;

    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: trimmed })
      .run();
  };

  return (
    <div className="notes-editor flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-1 border-b border-dropdownDivider px-4 py-2">
        {toolbarButtons.map(({ label, icon: Icon, isActive, run }) => {
          const active = editor ? isActive(editor) : false;
          return (
            <button
              key={label}
              type="button"
              onClick={() => editor && run(editor)}
              disabled={!editor || disabled}
              className={[
                "rounded-md border p-1.5 transition disabled:cursor-not-allowed disabled:opacity-50",
                active
                  ? "border-velBlue/35 bg-velBlue/15 text-velBlue"
                  : "border-transparent text-mutedText hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText",
              ].join(" ")}
              aria-label={label}
              title={label}
            >
              <Icon strokeWidth={1.5} className="h-4 w-4" />
            </button>
          );
        })}

        <span className="mx-1 h-5 w-px bg-dropdownDivider" aria-hidden />

        <button
          type="button"
          onClick={setLink}
          disabled={!editor || disabled}
          className={[
            "rounded-md border p-1.5 transition disabled:cursor-not-allowed disabled:opacity-50",
            editor?.isActive("link")
              ? "border-velBlue/35 bg-velBlue/15 text-velBlue"
              : "border-transparent text-mutedText hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText",
          ].join(" ")}
          aria-label="Link"
          title="Link"
        >
          <Link strokeWidth={1.5} className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-transparent">
        <EditorContent editor={editor} className="min-h-full" />
      </div>

      <style>{NOTES_EDITOR_STYLES}</style>
    </div>
  );
};

export default NotesEditor;
